import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutationModalContext } from '../../../contexts/MutationModalProvider';
import useManualTransactionActions from '../../../hooks/APIHooks/finance/useManualTransactionActions';
import { ManualTransactionHistory } from '../../../types/history';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import CreateEditTransactionModal from '../../CommonComps/Modals/CreateEditTransactionModal';

const TransactionModalsWrapper = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of custom API hooks here
  const { deleteManualTransactionHandler } = useManualTransactionActions('');

  // integration of context hooks here
  const {
    data,
    showUpdateModal,
    setShowUpdateModal,
    showDeleteModal,
    setShowDeleteModal,
  } = useMutationModalContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete transaction feature declared here
  const deleteTransactionHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteManualTransactionHandler
        .mutateAsync({ tranId: (data as ManualTransactionHistory).id })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Transaction Deleted!!');

            setShowDeleteModal(false);

            queryClient.invalidateQueries({
              queryKey: ['manual-transaction-list'],
            });
          } else {
            toast.error(res.data.message || 'Transaction Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering transaction modals wrapper section component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdateModal && (
          <CreateEditTransactionModal
            title='create'
            manualTransaction={data as ManualTransactionHistory}
            handleModalClose={() => setShowUpdateModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal ? (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this transaction? Once you
                delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteTransactionHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
};

export default TransactionModalsWrapper;
